'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// One item per root: this is not an accordion between cards, each card opens and
// closes on its own. `type="multiple"` is what makes a lone item independent.
const ITEM_VALUE = 'card';

interface CollapsibleCardProps {
  title: ReactNode;
  /** Controlled. Owners get it from `useSummaryCollapse`. */
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  /**
   * Controls shown at the right of the header (an edit pencil, a primary action).
   * They sit outside the toggle button, so clicking them never folds the card.
   */
  actions?: ReactNode;
  headerClassName?: string;
  contentClassName?: string;
  children: ReactNode;
}

/**
 * A details-page card whose body folds away into its header.
 *
 * Built on the Radix accordion primitives rather than `ui/accordion.tsx`: those
 * wrappers are shaped for the bordered list in CurrentAccount (trailing chevron,
 * `border-b`, their own paddings), and a card header that carries actions next to
 * the toggle would have to override all of it. The primitives still bring what
 * matters — the heading/button/region wiring, and the open/close animation.
 */
export function CollapsibleCard({
  title,
  collapsed,
  onCollapsedChange,
  actions,
  headerClassName,
  contentClassName,
  children,
}: CollapsibleCardProps) {
  return (
    <AccordionPrimitive.Root
      type="multiple"
      value={collapsed ? [] : [ITEM_VALUE]}
      onValueChange={(open) => onCollapsedChange(open.length === 0)}
      asChild
    >
      <Card>
        <AccordionPrimitive.Item value={ITEM_VALUE}>
          <CardHeader
            className={cn(
              'flex flex-row flex-wrap items-center justify-between gap-2 space-y-0',
              headerClassName,
            )}
          >
            {/* Header renders the <h3>, Trigger the <button> inside it: the card
                titles stay in the page's heading outline. */}
            <AccordionPrimitive.Header asChild>
              <CardTitle className="min-w-0 flex-1 text-base sm:text-lg">
                <AccordionPrimitive.Trigger className="-m-1 flex w-full items-center gap-2 rounded-md p-1 text-left transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 [&[data-state=closed]>svg]:-rotate-90">
                  <ChevronDown
                    aria-hidden="true"
                    className="h-5 w-5 shrink-0 text-gray-500 transition-transform duration-200"
                  />
                  {title}
                </AccordionPrimitive.Trigger>
              </CardTitle>
            </AccordionPrimitive.Header>
            {actions}
          </CardHeader>
          <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <CardContent className={contentClassName}>{children}</CardContent>
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      </Card>
    </AccordionPrimitive.Root>
  );
}
